import { useEffect, useRef, useState } from "preact/hooks";
import { type Account, post, rememberAccount } from "../account.ts";

const INPUT_BYTES = 5 * 1024 * 1024;
const PHOTO_BYTES = 100 * 1024;

/** Decode locally, crop the centre and discard the original file and metadata. */
async function preparePhoto(file: File): Promise<string> {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("Choose a JPEG, PNG or WebP image.");
  if (file.size > INPUT_BYTES) throw new Error("Choose a photo no larger than 5 MiB.");
  const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const jpeg = header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;
  const png = [137, 80, 78, 71, 13, 10, 26, 10].every((byte, i) => header[i] === byte);
  const webp = String.fromCharCode(...header.slice(0, 4)) === "RIFF" && String.fromCharCode(...header.slice(8, 12)) === "WEBP";
  if (!(file.type === "image/jpeg" ? jpeg : file.type === "image/png" ? png : webp)) throw new Error("That file isn't a supported JPEG, PNG or WebP image.");
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    const loaded = Promise.withResolvers<void>();
    image.onload = () => loaded.resolve();
    image.onerror = () => loaded.reject(new Error("That image couldn't be opened. Choose another JPEG, PNG or WebP photo."));
    image.src = url;
    await loaded.promise;
    if (!image.naturalWidth || !image.naturalHeight) throw new Error("That image couldn't be opened. Choose another photo.");
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 256;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("This browser couldn't prepare the photo. Try another browser.");
    context.fillStyle = "#fff";
    context.fillRect(0, 0, 256, 256);
    const edge = Math.min(image.naturalWidth, image.naturalHeight);
    context.drawImage(image, (image.naturalWidth - edge) / 2, (image.naturalHeight - edge) / 2, edge, edge, 0, 0, 256, 256);
    for (const quality of [0.9, 0.75, 0.6, 0.45, 0.3, 0.15]) {
      const encoded = Promise.withResolvers<Blob | null>();
      canvas.toBlob(encoded.resolve, "image/jpeg", quality);
      const blob = await encoded.promise;
      if (!blob || blob.type !== "image/jpeg") throw new Error("This browser couldn't prepare a JPEG photo.");
      if (blob.size > PHOTO_BYTES) continue;
      const prepared = Promise.withResolvers<string>();
      const reader = new FileReader();
      reader.onload = () => prepared.resolve(reader.result as string);
      reader.onerror = () => prepared.reject(new Error("That photo couldn't be prepared. Choose it again."));
      reader.readAsDataURL(blob);
      return await prepared.promise;
    }
    throw new Error("That photo couldn't fit within 100 KiB. Choose a simpler image.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

function ProfilePhoto(props: { image: string | null; name: string; preview?: boolean }) {
  const [failed, setFailed] = useState<string | null>(null);
  const initial = Array.from(props.name.trim())[0]?.toLocaleUpperCase() ?? "?";
  return (
    <span class="profile-photo">
      {props.image && failed !== props.image ? (
        <img src={props.image} alt={props.preview ? "Profile photo preview" : "Profile photo"} referrerPolicy="no-referrer" onError={() => setFailed(props.image)} />
      ) : (
        <span role="img" aria-label={props.image ? `Photo unavailable; initial ${initial}` : `No profile photo; initial ${initial}`}>{initial}</span>
      )}
    </span>
  );
}

function ProfileForm(props: { account: Account; online: boolean; onCancel: () => void; onSaved: (account: Account, remembered: boolean) => void }) {
  const [name, setName] = useState(props.account.name);
  // Undefined is deliberately different from null: a name-only save preserves a Google photo.
  const [image, setImage] = useState<string | null | undefined>(undefined);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const preparing = useRef(false);
  const submitting = useRef(false);
  useEffect(() => () => { generation.current++; }, []);
  const choosePhoto = async (file: File | undefined) => {
    if (!file || !props.online || submitting.current) return;
    const current = ++generation.current;
    preparing.current = true;
    setProcessing(true);
    setError(null);
    try {
      const photo = await preparePhoto(file);
      if (current === generation.current) setImage(photo);
    } catch (x) {
      if (current === generation.current) setError((x as Error).message);
    } finally {
      if (current === generation.current) {
        preparing.current = false;
        setProcessing(false);
      }
    }
  };
  const cancel = () => {
    generation.current++;
    props.onCancel();
  };
  const save = async (event: Event) => {
    event.preventDefault();
    if (preparing.current || submitting.current || !props.online) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > 100) {
      setError("Enter a display name between 1 and 100 characters.");
      return;
    }
    submitting.current = true;
    setSaving(true);
    setError(null);
    try {
      const { user } = await post<{ user: Account }>("/api/me/profile", { name: trimmed, ...(image !== undefined ? { image } : {}) });
      let remembered = true;
      try {
        rememberAccount(user);
      } catch {
        remembered = false;
      }
      props.onSaved(user, remembered);
    } catch (x) {
      setError(`${(x as Error).message} Your draft hasn't been discarded. Reconnect and save again, or cancel.`);
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };
  const disabled = !props.online || saving || processing;
  const preview = image === undefined ? props.account.image : image;
  return (
    <form class="card stack profile-form" onSubmit={save} aria-label="Edit profile" aria-busy={saving || processing}>
      <label class="field" for="profile-name">
        Display name
        <input id="profile-name" name="name" autoComplete="name" required maxLength={100} value={name} disabled={disabled} onInput={(event) => setName(event.currentTarget.value)} />
      </label>
      <div class="profile-photo-row">
        <ProfilePhoto image={preview} name={name} preview />
        <div class="stack profile-photo-controls">
          <label class="field" for="profile-photo">
            Profile photo (optional)
            <input id="profile-photo" type="file" accept="image/jpeg,image/png,image/webp" disabled={disabled} aria-describedby="profile-photo-help" onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = "";
              void choosePhoto(file);
            }} />
          </label>
          <p id="profile-photo-help" class="hint">JPEG, PNG or WebP, up to 5 MiB. Photos are centre-cropped to a square and saved as a 256 × 256 JPEG (up to 100 KiB).</p>
          <div><button type="button" disabled={disabled || !preview} onClick={() => { setImage(null); setError(null); }}>Remove photo</button></div>
        </div>
      </div>
      {error && <p class="error" role="alert">{error}</p>}
      {!props.online && <p class="hint" role="status">Connect to edit or save your profile. Profile changes aren't queued offline.</p>}
      {processing && <p class="hint" role="status">Preparing photo…</p>}
      {saving && <p class="hint" role="status">Saving profile…</p>}
      <div class="row-buttons">
        <button type="button" disabled={saving} onClick={cancel}>Cancel</button>
        <button type="submit" class="primary" disabled={disabled || !name.trim()}>Save</button>
      </div>
    </form>
  );
}

export function AccountProfile(props: { account: Account; online: boolean }) {
  const [account, setAccount] = useState(props.account);
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  return (
    <>
      <div class="profile-summary">
        <ProfilePhoto image={account.image} name={account.name} />
        <div class="stack profile-identity">
          <strong>{account.name}</strong>
          <p>{account.email} <span class="hint">· signs in with {account.signInMethod === "google" ? "Google" : "email links"}</span></p>
        </div>
        {!editing && <button type="button" disabled={!props.online} onClick={() => { setStatus(null); setEditing(true); }}>Edit profile</button>}
      </div>
      {status && <p role="status">{status}</p>}
      {!props.online && !editing && <p class="hint">Connect to edit your profile. Profile changes aren't queued offline.</p>}
      {editing && <ProfileForm account={account} online={props.online} onCancel={() => setEditing(false)} onSaved={(saved, remembered) => {
        setAccount(saved);
        setEditing(false);
        setStatus(remembered ? "Profile saved." : "Profile saved, but this browser couldn't remember it for offline use.");
      }} />}
    </>
  );
}
